/**
 * The "..." menu of a named list or one of its items (#358).
 *
 * The list and item views live inside ProseMirror's own DOM, so a menu
 * rendered there sat under the editor's event handling: a tap outside it
 * closed it AND landed on what was underneath - often another item's
 * checkbox, which ticked. The menu now renders in a portal on document.body,
 * fixed at its trigger, with a DismissBackdrop just under it (the ListPicker /
 * FlagMenu pattern from #121). A tap outside hits the backdrop, which only
 * closes the menu.
 */
import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { DismissBackdrop } from '../../DismissBackdrop';

const GAP = 4;
const EDGE = 8;

interface NamedListMenuProps {
  anchor: RefObject<HTMLElement | null>;
  label: string;
  /** Tap outside or Escape. */
  onDismiss: () => void;
  children: ReactNode;
}

export function NamedListMenu({ anchor, label, onDismiss, children }: NamedListMenuProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  // Right-align under the trigger, kept on screen; flip above when it would
  // run off the bottom.
  useLayoutEffect(() => {
    const el = ref.current;
    const a = anchor.current;
    if (!el || !a) return;
    const r = a.getBoundingClientRect();
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const left = Math.max(EDGE, Math.min(r.right - w, vw - w - EDGE));
    let top = r.bottom + GAP;
    if (top + h > vh - EDGE) top = r.top - h - GAP;
    top = Math.max(EDGE, Math.min(top, vh - h - EDGE));
    setPos({ left, top });
  }, [anchor]);

  useEffect(() => {
    ref.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus({ preventScroll: true });
  }, []);

  // A resize moves the trigger out from under the fixed menu; close it. (The
  // backdrop covers the page, so it cannot be scrolled while the menu is open.)
  useEffect(() => {
    const close = () => onDismiss();
    window.addEventListener('resize', close);
    return () => window.removeEventListener('resize', close);
  }, [onDismiss]);

  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onDismiss(); return; }
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const items = [...(ref.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [])];
    if (!items.length) return;
    e.preventDefault();
    const i = items.indexOf(document.activeElement as HTMLElement);
    items[(i + (e.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length].focus();
  };

  return createPortal(
    <>
      <DismissBackdrop zClass="z-[59]" onDismiss={onDismiss} />
      <div
        ref={ref}
        role="menu"
        aria-label={label}
        onKeyDown={onKey}
        className="named-list-menu fixed z-[60]"
        style={{ left: pos?.left ?? 0, top: pos?.top ?? 0, visibility: pos ? 'visible' : 'hidden' }}
      >
        {children}
      </div>
    </>,
    document.body,
  );
}
